import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { FormGroup, FormBuilder, Validators, ValidatorFn, AbstractControl } from '@angular/forms';
import { InitialData } from '../models/initialData.model';
import { InitialService } from '../Services/app.initial.service';
import { IktvaFormService } from '../Services/app-iktvaForm.service';
import { IktvaForm } from '../models/iktvaForm.model';
import { Service } from '../models/service.model';
import { NgxSpinnerService } from 'ngx-spinner';
import { FormDialogComponent } from '../formDialog/formDialog.component';
import { Router, ActivatedRoute } from '@angular/router';
import { Observable, forkJoin, EMPTY } from 'rxjs';
import { DataMigrationService } from '../Services/dataMigration.service';
import { IktvaFile } from '../models/iktvaFileModel';
import { HttpClient } from '@angular/common/http';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css'],
  standalone: false
})
export class HomeComponent implements OnInit {

  initialData!: InitialData;

  formGeneral!: FormGroup;
  formManufacturing!: FormGroup;  
  formCertifications!: FormGroup;
  formIKTVA!: FormGroup;
  formSourcing!: FormGroup;

  iktvaReportValues: string[] = [];
  businessSegments: string[] = [];
  yesNo: string[] = [];
  servicesProvided: string[] = [];
  localOrImported: string[] = [];
  rawMaterialSource: string[] = [];

  uploadFiles: File[] = [];
  uploadFileNames: string[] = [];
  uploadFilesAuditReport: File[] = [];
  uploadFileAuditReportNames: string[] = [];
  uploadFilesIktvaPlan: File[] = [];
  uploadFilesIktvaPlanNames: string[] = [];

  spinnerText: string = '';
  formMode: string = 'new';

  oldForm: IktvaForm | null = null;
  currentYear: number = new Date().getFullYear();
  previousYear: number = new Date().getFullYear() - 1;

  previouslyUploadedQualityFiles: IktvaFile[] = [];
  previouslyUploadedManufacturingFiles: IktvaFile[] = [];
  previouslyUploadedIktvaPlanFiles: IktvaFile[] = [];

  isOtherServicesVisible: boolean = false;

  ngOnInit(): void {
    this.previouslyUploadedQualityFiles = [];
    this.previouslyUploadedManufacturingFiles = [];
    this.previouslyUploadedIktvaPlanFiles = [];
    this.currentYear = new Date().getFullYear();
    this.oldForm = null;
    this.formMode = "new";
    this.isOtherServicesVisible = false;

    // Read initialData from the route resolver — guaranteed to be available
    // synchronously before this component is instantiated, eliminating the
    // race condition that caused *ngIf="initialData" to be false on first click.
    this.initialData = this.route.snapshot.data['initialData'] as InitialData;
    this.initialData.YesNoTypes = [{ Name: 'Yes', Value: true }, { Name: 'No', Value: false }];

    this.dataMigrationService.iktvaFormData = null;
    this.uploadFiles = [];
    this.uploadFileNames = [];
    this.uploadFilesAuditReport = [];
    this.uploadFileAuditReportNames = [];
    this.uploadFilesIktvaPlan = [];
    this.uploadFilesIktvaPlanNames = [];
    

    this.formGeneral = this.fb.group({
      'chkFirstTime': [null, Validators.required],
      'iktvaUniqueId': ['', Validators.required],
      'companyName': ['', Validators.required],
      'commercialRegistration': [''],
      'halVendorNumber': '',
      'businessSegment': [null, Validators.required],
      'businessDescription': ['', Validators.required],
      'address': ['', Validators.required],
      'city': ['', Validators.required],
      'country': ['', Validators.required],
      'companyEmail': ['', [Validators.required, Validators.email]],
      'companyContactPerson': ['', Validators.required],
      'phoneContactPerson': ['', Validators.required],
      'emailContactPerson': ['', [Validators.required, Validators.email]],
      'chkAnnualRevenue': [null, Validators.required],
      'chkTotalEmployee': [null, Validators.required]
    });

    this.formManufacturing = this.fb.group({
      'totalEmployees': ['', [Validators.required, Validators.pattern('^[0-9]*$')]],
      'totalSaudiEmployees': ['', [Validators.required, Validators.pattern('^[0-9]*$')]],
      'totalQualityEmployees': ['', [Validators.required, Validators.pattern('^[0-9]*$')]],
      'totalManufacturingEmployees': ['', [Validators.required, Validators.pattern('^[0-9]*$')]],
      'totalEngineeringEmployees': ['', [Validators.required, Validators.pattern('^[0-9]*$')]],
      'manufacturingDivArea': ['', [Validators.required, Validators.pattern('^[0-9]*$')]]
    });

    this.formCertifications = this.fb.group({
      'chkApiCertification': [null, Validators.required],
      'chkSmeCertification': [null, Validators.required],
      'chkIsoCertification': [null, Validators.required],
      'chkErpSystem': [null, Validators.required]
    });

    this.formIKTVA = this.fb.group({
      'chkAnnualAuditReport': [null, Validators.required],
      'chkExporting': [null, Validators.required],
      'chkRequireHalSupport': [null, Validators.required],
      'iktvaPreviousYearScore': [''],
      'previousYearRevenue': [''],
      'previousYearScoreAuditReport': [],
      'currentYearEstimatedScore': [''],
      'currentYearEstimatedRevenue': [''],
      'servicesProvided': ['', Validators.required],
      'otherServices': [''],
      'localOrImported': [null, Validators.required],
      'spendLocalizedServices': ['', Validators.required],
      'spendCompensationSaudiNationals': ['', Validators.required],
      'spendTrainingSaudiNationals': ['', Validators.required],
      'spendSupplierDevelopment': ['', Validators.required],
      'spendResearch': ['', Validators.required],
      'iktvaPlan': [''],
      'iktvaFileUpload': [null, this.validateIktvaFileUploadControl()]
    });

    this.formSourcing = this.fb.group({
      'majorContracts': [''],
      'rawMaterialSource': [null],
      'chkQualityManagement': [null, Validators.required],
      'chkInternalQualityAudit': [null, Validators.required]
    });
  }

  constructor(
    private fb: FormBuilder,
    private initService: InitialService,
    private iktvaService: IktvaFormService,
    private spinner: NgxSpinnerService,
    public dialog: MatDialog,
    private router: Router,
    private route: ActivatedRoute,
    private dataMigrationService: DataMigrationService,
    private http: HttpClient
  ) {
  }

  public validateIktvaFileUploadControl(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      console.log("validating!");
      if (this.previouslyUploadedIktvaPlanFiles && this.previouslyUploadedIktvaPlanFiles.length > 0)
        return null;
      if (this.uploadFilesIktvaPlan.length === 0) {
        return { 'iktvaPlanFileRequired': { value: control.value } }
      }
      return null;
    }
  }

  public buildIktvaFormObject(): IktvaForm | null {
    let iktva: IktvaForm | null = null;
    if (this.checkIfFormIsValid()) {
      iktva = new IktvaForm();
      
      // Id is left as null (default) for new forms
      iktva.CompanyName = this.formGeneral.controls['companyName'].value;
      iktva.CommercialRegistrationNumber = this.formGeneral.controls['commercialRegistration'].value;
      iktva.HalVendorNumber = this.formGeneral.controls['halVendorNumber'].value;
      iktva.BusinessSegmentId = +this.formGeneral.controls['businessSegment'].value;
      iktva.BusinessDescription = this.formGeneral.controls['businessDescription'].value;
      iktva.Address = this.formGeneral.controls['address'].value;
      iktva.City = this.formGeneral.controls['city'].value;
      iktva.Country = this.formGeneral.controls['country'].value;
      iktva.CompanyEmail = this.formGeneral.controls['companyEmail'].value;
      iktva.CompanyContactPerson = this.formGeneral.controls['companyContactPerson'].value;
      iktva.PhoneContactPerson = this.formGeneral.controls['phoneContactPerson'].value;
      iktva.EmailContactPerson = this.formGeneral.controls['emailContactPerson'].value;
      iktva.AnnualRevenueMoreThan = this.getBoolValue(this.formGeneral.controls['chkAnnualRevenue'].value);
      iktva.TotalEmployeesMoreThan = this.getBoolValue(this.formGeneral.controls['chkTotalEmployee'].value);

      iktva.TotalEmployees = +this.formManufacturing.controls['totalEmployees'].value;
      iktva.TotalSaudiEmployees = +this.formManufacturing.controls['totalSaudiEmployees'].value;
      iktva.TotalQualityEmployees = +this.formManufacturing.controls['totalQualityEmployees'].value;
      iktva.TotalManufacturingEmployees = +this.formManufacturing.controls['totalManufacturingEmployees'].value;
      iktva.TotalEngineeringEmployees = +this.formManufacturing.controls['totalEngineeringEmployees'].value;
      iktva.ManufacturingDivArea = +this.formManufacturing.controls['manufacturingDivArea'].value;

      iktva.ApiCertification = this.getBoolValue(this.formCertifications.controls['chkApiCertification'].value);
      iktva.SmeCertification = this.getBoolValue(this.formCertifications.controls['chkSmeCertification'].value);
      iktva.IsoCertification = this.getBoolValue(this.formCertifications.controls['chkIsoCertification'].value);
      iktva.ErpSystem = this.getBoolValue(this.formCertifications.controls['chkErpSystem'].value);

      iktva.AnnualAuditReportToAramco = this.getBoolValue(this.formIKTVA.controls['chkAnnualAuditReport'].value);
      iktva.ExportingOutsideSaudi = this.getBoolValue(this.formIKTVA.controls['chkExporting'].value);
      iktva.RequireHalSupport = this.getBoolValue(this.formIKTVA.controls['chkRequireHalSupport'].value);
      iktva.PreviousYearIktvascore = this.formIKTVA.controls['iktvaPreviousYearScore'].value;
      iktva.PreviousYearRevenue = this.formIKTVA.controls['previousYearRevenue'].value;
      iktva.PreviousYearReportType = +this.formIKTVA.controls['previousYearScoreAuditReport'].value;
      iktva.CurrentYearEstimatedScore = this.formIKTVA.controls['currentYearEstimatedScore'].value;
      iktva.CurrentYearEstimatedRevenue = this.formIKTVA.controls['currentYearEstimatedRevenue'].value;
      iktva.ServicesProvidedIds = this.formIKTVA.controls['servicesProvided'].value;
      iktva.OtherService = this.formIKTVA.controls['otherServices'].value;
      iktva.MaterialProductionTypeId = +this.formIKTVA.controls['localOrImported'].value;
      iktva.SpendLocalizedServices = this.formIKTVA.controls['spendLocalizedServices'].value;
      iktva.SpendCompensationSaudiNationals = this.formIKTVA.controls['spendCompensationSaudiNationals'].value;
      iktva.SpendTrainingSaudiNationals = this.formIKTVA.controls['spendTrainingSaudiNationals'].value;
      iktva.SpendSupplierDevelopment = this.formIKTVA.controls['spendSupplierDevelopment'].value;
      iktva.SpendResearch = this.formIKTVA.controls['spendResearch'].value;
      iktva.Iktvaplan = this.formIKTVA.controls['iktvaPlan'].value;

      iktva.MajorContracts = this.formSourcing.controls['majorContracts'].value;
      iktva.RawMaterialSourceId = +this.formSourcing.controls['rawMaterialSource'].value;
      iktva.QualityManagementSystem = this.getBoolValue(this.formSourcing.controls['chkQualityManagement'].value);
      iktva.InternalQualityAudit = this.getBoolValue(this.formSourcing.controls['chkInternalQualityAudit'].value);

    }
    return iktva;
  }

  public getBoolValue(value: string): boolean {
    if (value && (value.toLowerCase() === 'true'))
      return true;
    return false;
  }

  public removeFile(file: File) {
    const index = this.uploadFiles.indexOf(file);
    this.uploadFiles.splice(index, 1);
    this.uploadFileNames.splice(index, 1);
  }

  public fileChange(event: Event) {
    const target = event.target as HTMLInputElement;
    if (!target.files) return;
    
    if (this.uploadFiles.length + target.files.length > 5) {
      alert("Maximum 5 number of files accepted!");
      return;
    }
    if (target.files.length > 0) {
      let totalSize = 0;
      for (let i = 0; i < this.uploadFiles.length; i++) {
        totalSize += this.uploadFiles[i].size;
      }
      for (let i = 0; i < target.files.length; i++) {
        totalSize += target.files.item(i)!.size;
      }
      if (totalSize > 1048576 * 10) {
        alert("Please upload files with total size less than 10 MB!");
        return;
      }
    }
    for (let i = 0; i < target.files.length; i++) {
      const file = target.files.item(i);
      if (file && this.uploadFileNames.indexOf(file.name) === -1) {
        this.uploadFiles.push(file);
        this.uploadFileNames.push(file.name);
      }
    }
  }

  public removeIktvaPlanFile(file: File) {
    const index = this.uploadFilesIktvaPlan.indexOf(file);
    this.uploadFilesIktvaPlan.splice(index, 1);
    this.uploadFilesIktvaPlanNames.splice(index, 1);
    this.formIKTVA.controls['iktvaFileUpload'].updateValueAndValidity({ onlySelf: true });
  }

  public fileChangeIktvaPlan(event: Event) {
    const target = event.target as HTMLInputElement;
    if (!target.files) return;
    
    if (this.uploadFilesIktvaPlan.length + target.files.length > 3) {
      alert("Maximum 3 number of files accepted!");
      return;
    }
    if (target.files.length > 0) {
      let totalSize = 0;
      for (let i = 0; i < this.uploadFilesIktvaPlan.length; i++) {
        totalSize += this.uploadFilesIktvaPlan[i].size;
      }
      for (let i = 0; i < target.files.length; i++) {
        totalSize += target.files.item(i)!.size;
      }
      if (totalSize > 1048576 * 10) {
        alert("Please upload files with total size less than 10 MB!");
        return;
      }
    }
    for (let i = 0; i < target.files.length; i++) {
      const file = target.files.item(i);
      if (file && this.uploadFilesIktvaPlanNames.indexOf(file.name) === -1) {
        this.uploadFilesIktvaPlan.push(file);
        this.uploadFilesIktvaPlanNames.push(file.name);
      }
    }
    this.formIKTVA.controls['iktvaFileUpload'].updateValueAndValidity({ onlySelf: true });
  }

  public removeAuditReportFile(file: File) {
    const index = this.uploadFilesAuditReport.indexOf(file);
    this.uploadFilesAuditReport.splice(index, 1);
    this.uploadFileAuditReportNames.splice(index, 1);
  }

  public fileChangeAuditReport(event: Event) {
    const target = event.target as HTMLInputElement;
    if (!target.files) return;
    
    if (this.uploadFilesAuditReport.length + target.files.length > 5) {
      alert("Maximum 5 number of files accepted!");
      return;
    }
    if (target.files.length > 0) {
      let totalSize = 0;
      for (let i = 0; i < this.uploadFilesAuditReport.length; i++) {
        totalSize += this.uploadFilesAuditReport[i].size;
      }
      for (let i = 0; i < target.files.length; i++) {
        totalSize += target.files.item(i)!.size;
      }
      if (totalSize > 1048576 * 10) {
        alert("Please upload files with total size less than 10 MB!");
        return;
      }
    }
    for (let i = 0; i < target.files.length; i++) {
      const file = target.files.item(i);
      if (file && this.uploadFileAuditReportNames.indexOf(file.name) === -1) {
        this.uploadFilesAuditReport.push(file);
        this.uploadFileAuditReportNames.push(file.name);
      }
    }
  }  

  public checkIfFormIsValid(): boolean {
    if (this.formCertifications.valid && this.formGeneral.valid && this.formIKTVA.valid && this.formManufacturing.valid && this.formSourcing.valid) {
      return true;
    }
    return false;
  }

  public submitForm() {
    this.spinnerText = 'Submitting the form..';
    this.spinner.show();
    let form = this.buildIktvaFormObject();
    if (form) {
      if (this.formMode === "new") {
        form.Id = null;
        this.iktvaService.submitForm(form).subscribe(
          (data) => {
            console.log(data);
            this.spinnerText = 'Uploading files..';
            forkJoin([this.uploadManufacturingFiles(data), this.uploadQualityAuditFiles(data), this.uploadIktvaPlanFiles(data)]).pipe().subscribe((res) => {
              this.hideSpinnerAndShowModal(data);
            }, error => {
                alert('Something unexpected happened. Please try again after some time.');
                this.spinner.hide();
            });
          },
          error => {
            alert('Something unexpected happened. Please try again after some time.');
            this.spinnerText = '';
            console.log(error);
          }
        );
      }
      else {
        form.Id = this.oldForm!.Id;
        form.UniqueIktvaId = this.oldForm!.UniqueIktvaId;
        form.Year = ''+new Date().getFullYear();
        this.iktvaService.updateForm(form).subscribe((data) => {
          if (data) {
            forkJoin([this.uploadManufacturingFiles(data), this.uploadQualityAuditFiles(data), this.uploadIktvaPlanFiles(data)]).pipe().subscribe((res) => {
              this.hideSpinnerAndShowModal(data);
            }, error => {
              alert('Something unexpected happened. Please try again after some time.');
            });
          }
          else {
            alert('Something unexpected happened. Please try again after some time.');
          }
        });
      }
    }
  }

  getServicesProvided(value: number[]): string {
    if (!value)
      return '';
    let servicesProvided = '';
    this.initialData.Services.forEach(x => {
      if (value.indexOf(x.Id) > -1) {
        const found = this.initialData.Services.find(l => l.Id === x.Id);
        if (found) {
          servicesProvided = servicesProvided + ', ' + found.Name;
        }
      }
    })
    return servicesProvided.slice(1);
  }

  getMateriaqlProductionType(value: number): string {
    const found = this.initialData.MaterialProductionTypes.find(x => x.Id === value);
    return found ? found.Name : '';
  }

  getSourceOfRawMaterial(value: number): string {
    const found = this.initialData.RawMaterialSources.find(x => x.Id === value);
    return found ? found.Name : '';
  }

  getReportingType(value: number): string {
    const found = this.initialData.IKTVAReportingTypes.find(x => x.Id === value);
    return found ? found.Name : '';
  }

  private hideSpinnerAndShowModal(data: IktvaForm) {
      this.spinnerText = '';
      this.spinner.hide();
      this.dataMigrationService.iktvaFormData = data;
    this.router.navigate(['/success']);
  }

  public uploadQualityAuditFiles(data: IktvaForm): Observable<boolean> {
    return this.iktvaService.uploadQualityAuditFiles(data.UniqueIktvaId, this.uploadFilesAuditReport);
  }

  public uploadManufacturingFiles(data: IktvaForm): Observable<boolean> {
    return this.iktvaService.uploadManufacturingFiles(data.UniqueIktvaId, this.uploadFiles);
  }

  public uploadIktvaPlanFiles(data: IktvaForm): Observable<boolean> {
    return this.iktvaService.uploadIktvaPlanFiles(data.UniqueIktvaId, this.uploadFilesIktvaPlan);
  }

  public chkFirstTimeChanged() {
    const chk: string = this.formGeneral.controls['chkFirstTime'].value;
    if (chk.toLowerCase() === "true") {
      this.formGeneral.controls['iktvaUniqueId'].disable();
      this.formGeneral.controls['iktvaUniqueId'].setValue('');
      this.formMode = "new";
      this.oldForm = null;
    }
    else {
      this.formGeneral.controls['iktvaUniqueId'].enable();
    }
  }

  public fetchForm(formId: string) {    
    this.spinner.show();
    if (formId && formId !== '')
      this.iktvaService.fetchForm(formId).subscribe((data) => {
        console.log(data);
        this.spinner.hide();
        if (data === null)
          alert('No data found for this ID. Make sure you are using correct ID');
        this.oldForm = data;
        if (+this.oldForm.Year === new Date().getFullYear())
          this.formMode = "edit";
        else
          this.formMode = "new";
        this.setUpForm(data);
      });
  }

  public findInvalidControls() {
    for (const xy in this.formIKTVA.controls) {
      if (this.formIKTVA.controls[xy].invalid)
        console.log(xy);
    }
  }

  public servicesChanged(value: string) {
    const otherService = this.initialData.Services.find(x => x.Name.toUpperCase() === "OTHER");
    if (otherService && this.formIKTVA.controls['servicesProvided'].value.findIndex((x: number) => x === otherService.Id) > -1) {
      this.formIKTVA.controls['otherServices'].enable();
      this.formIKTVA.controls['otherServices'].setValidators([Validators.required]);
      this.isOtherServicesVisible = true;
    }
    else {
      this.isOtherServicesVisible = false;
      this.formIKTVA.controls['otherServices'].disable();
      this.formIKTVA.controls['otherServices'].clearValidators();
    }
  }

  public setUpForm(form: IktvaForm) {
    this.formGeneral.controls['companyName'].setValue(form.CompanyName);
    this.formGeneral.controls['commercialRegistration'].setValue(form.CommercialRegistrationNumber);
    this.formGeneral.controls['halVendorNumber'].setValue(form.HalVendorNumber);
    this.formGeneral.controls['businessSegment'].setValue(form.BusinessSegmentId);
    this.formGeneral.controls['businessDescription'].setValue(form.BusinessDescription);
    this.formGeneral.controls['address'].setValue(form.Address);
    this.formGeneral.controls['city'].setValue(form.City);
    this.formGeneral.controls['country'].setValue(form.Country);
    this.formGeneral.controls['companyEmail'].setValue(form.CompanyEmail);
    this.formGeneral.controls['companyContactPerson'].setValue(form.CompanyContactPerson);
    this.formGeneral.controls['phoneContactPerson'].setValue(form.PhoneContactPerson);
    this.formGeneral.controls['emailContactPerson'].setValue(form.EmailContactPerson);
    this.formGeneral.controls['chkAnnualRevenue'].setValue(''+form.AnnualRevenueMoreThan);
    this.formGeneral.controls['chkTotalEmployee'].setValue(''+form.TotalEmployeesMoreThan);

    this.formManufacturing.controls['totalEmployees'].setValue(form.TotalEmployees);
    this.formManufacturing.controls['totalSaudiEmployees'].setValue(form.TotalSaudiEmployees);
    this.formManufacturing.controls['totalQualityEmployees'].setValue(form.TotalQualityEmployees);
    this.formManufacturing.controls['totalManufacturingEmployees'].setValue(form.TotalManufacturingEmployees);
    this.formManufacturing.controls['totalEngineeringEmployees'].setValue(form.TotalEngineeringEmployees);
    this.formManufacturing.controls['manufacturingDivArea'].setValue(form.ManufacturingDivArea);

    this.formCertifications.controls['chkApiCertification'].setValue('' + form.ApiCertification);
    this.formCertifications.controls['chkSmeCertification'].setValue('' + form.SmeCertification);
    this.formCertifications.controls['chkIsoCertification'].setValue(''+form.IsoCertification);
    this.formCertifications.controls['chkErpSystem'].setValue(''+form.ErpSystem);

    this.formIKTVA.controls['chkAnnualAuditReport'].setValue(''+form.AnnualAuditReportToAramco);
    this.formIKTVA.controls['chkExporting'].setValue(''+form.ExportingOutsideSaudi);
    this.formIKTVA.controls['chkRequireHalSupport'].setValue(''+form.RequireHalSupport);
    this.formIKTVA.controls['iktvaPreviousYearScore'].setValue(form.PreviousYearIktvascore);
    this.formIKTVA.controls['previousYearRevenue'].setValue(form.PreviousYearRevenue);
    this.formIKTVA.controls['previousYearScoreAuditReport'].setValue(form.PreviousYearReportType);
    this.formIKTVA.controls['currentYearEstimatedScore'].setValue(form.CurrentYearEstimatedScore);
    this.formIKTVA.controls['currentYearEstimatedRevenue'].setValue(form.CurrentYearEstimatedRevenue);
    this.formIKTVA.controls['servicesProvided'].setValue(form.ServicesProvidedIds);
    const serviceFound = form.ServicesProvidedIds.find(x => x === 15);
    if (serviceFound !== undefined) {      
      this.formIKTVA.controls['otherServices'].setValue(form.OtherService);
    }    

    this.servicesChanged('');

    this.formIKTVA.controls['localOrImported'].setValue(form.MaterialProductionTypeId);
    this.formIKTVA.controls['spendLocalizedServices'].setValue(form.SpendLocalizedServices);
    this.formIKTVA.controls['spendCompensationSaudiNationals'].setValue(form.SpendCompensationSaudiNationals);
    this.formIKTVA.controls['spendTrainingSaudiNationals'].setValue(form.SpendTrainingSaudiNationals);
    this.formIKTVA.controls['spendSupplierDevelopment'].setValue(form.SpendSupplierDevelopment);
    this.formIKTVA.controls['spendResearch'].setValue(form.SpendResearch);
    this.formIKTVA.controls['iktvaPlan'].setValue(form.Iktvaplan);

    this.formSourcing.controls['majorContracts'].setValue(form.MajorContracts);
    this.formSourcing.controls['rawMaterialSource'].setValue(form.RawMaterialSourceId);
    this.formSourcing.controls['chkQualityManagement'].setValue(''+form.QualityManagementSystem);
    this.formSourcing.controls['chkInternalQualityAudit'].setValue('' + form.InternalQualityAudit);

    this.previouslyUploadedQualityFiles = form.IktvaFiles.filter(x => x.Type === 'QUALITY');
    this.previouslyUploadedManufacturingFiles = form.IktvaFiles.filter(x => x.Type === 'MANUFACTURING');
    this.previouslyUploadedIktvaPlanFiles = form.IktvaFiles.filter(x => x.Type === 'IKTVAPLAN');
  }

  // Handles the Next button on the LCGPA Status step.
  // The iktvaFileUpload control uses a custom validator that depends on a component
  // property (uploadFilesIktvaPlan.length), not the control value itself, so Angular
  // does not re-evaluate it automatically. Previously, updateValueAndValidity() was
  // called inline on the same button as matStepperNext, causing a race condition where
  // the stepper checked validity BEFORE the validator result propagated — requiring two
  // clicks. This method validates first, then navigates, all in a single click.
  public goToNextFromLcgpaStatus(stepper: MatStepper): void {
    const fileUploadControl = this.formIKTVA.controls['iktvaFileUpload'];
    fileUploadControl.markAsTouched();
    fileUploadControl.updateValueAndValidity({ onlySelf: false });
    if (this.formIKTVA.valid) {
      stepper.next();
    }
  }

  public resetStepper(stepper: MatStepper) {
    stepper.reset();
    stepper.selectedIndex = 0;
    this.uploadFilesAuditReport = [];
    this.uploadFileAuditReportNames = [];
    this.uploadFiles = [];
    this.uploadFileNames = [];
  }

  public downloadFile(fileId: string, fileName: string): void {
    this.spinnerText = 'Downloading file...';
    this.spinner.show();
    
    this.http.get(`api/download/${fileId}`, { 
      responseType: 'blob',
      observe: 'response'
    }).subscribe({
      next: (response) => {
        this.spinner.hide();
        
        // Create a blob from the response
        const blob = response.body;
        if (!blob) {
          alert('Failed to download file.');
          return;
        }
        
        // Create a download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        
        // Trigger download
        document.body.appendChild(link);
        link.click();
        
        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      },
      error: (error) => {
        console.error('Download error:', error);
        this.spinner.hide();
        
        if (error.status === 404) {
          alert('File not found. It may have been deleted or moved.');
        } else if (error.status === 401) {
          alert('You are not authorized to download this file.');
        } else {
          alert('Failed to download file. Please try again.');
        }
      }
    });
  }
}
